import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { ArrowLeft, CreditCard, Star, Shield, Globe, Calendar, User, Check, ExternalLink, XCircle } from 'lucide-react';
import MarketplaceLayout from '../../layouts/MarketplaceLayout';
import CheckoutModal from '../../components/CheckoutModal';

export default function AppDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [app, setApp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCheckout, setShowCheckout] = useState(false);
    const [isOwnApp, setIsOwnApp] = useState(false);

    useEffect(() => {
        fetchAppDetails();
        checkIfOwnApp();
    }, [slug]);

    useEffect(() => {
        // Check if we're returning from login for checkout
        const urlParams = new URLSearchParams(window.location.search);
        const isCheckout = urlParams.get('checkout') === 'true';
        const checkoutAppId = sessionStorage.getItem('checkoutAppId');

        if (isCheckout && checkoutAppId && app && app.id === parseInt(checkoutAppId)) {
            // Clear the stored data
            sessionStorage.removeItem('checkoutAppId');
            sessionStorage.removeItem('checkoutAppSlug');

            // Auto-open checkout modal
            setShowCheckoutModal(true);

            // Clear URL params
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [app]);

    const fetchAppDetails = async () => {
        try {
            const response = await api.get(`/api/marketplace/apps/by-slug/${slug}`);
            setApp(response.data);
        } catch (error) {
            console.error('Failed to fetch app details:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkIfOwnApp = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            // Get current user's apps
            const response = await api.get('/api/apps');
            const userApps = response.data;

            // Check if this app belongs to the current user
            const ownApp = userApps.some(userApp => userApp.slug === slug || userApp.id === parseInt(slug));
            setIsOwnApp(ownApp);
        } catch (error) {
            // Ignore 401/403 errors as they just mean the user isn't a developer owner
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                return;
            }
            console.error('Failed to check app ownership:', error);
        }
    };

    const handlePurchase = async () => {
        setShowCheckout(true); // Show checkout modal instead of alert
    };

    if (loading) {
        return (
            <MarketplaceLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </MarketplaceLayout>
        );
    }

    if (!app) {
        return (
            <MarketplaceLayout>
                <div className="text-center py-12">
                    <p className="text-gray-600">App not found</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="mt-4 text-blue-600 hover:underline"
                    >
                        Back to Marketplace
                    </button>
                </div>
            </MarketplaceLayout>
        );
    }

    return (
        <MarketplaceLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Back to Marketplace
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            {/* App Header */}
                            <div className="flex items-start gap-6 mb-8">
                                {app.icon_url ? (
                                    <img
                                        src={app.icon_url}
                                        alt={app.name}
                                        className="w-24 h-24 rounded-xl object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2UyZThmMCIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjUwIiB5PSI1MCIgc3R5bGU9ImZpbGw6IzY0NzQ4Yjtmb250LXdlaWdodDpib2xkO2ZvbnQtc2l6ZTozNnB4O2ZvbnQtZmFtaWx5OkFyaWFsLHNhbnMtc2VyaWYiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPkE8L3RleHQ+PC9zdmc+';
                                        }}
                                    />
                                ) : (
                                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                                        <span className="text-white text-3xl font-bold">
                                            {app.name.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h1 className="text-3xl font-bold text-gray-900">{app.name}</h1>
                                        {app.status === 0 && (
                                            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold flex items-center gap-1">
                                                <XCircle className="w-4 h-4" />
                                                Deactivated
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-lg text-gray-600 mb-4">{app.tagline || app.description}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {app.tags?.split(',').map(tag => (
                                            <span key={tag} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                                {tag.trim()}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Pricing Card */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-8">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">License Pricing</h3>
                                        <div className="text-3xl font-bold text-gray-900">
                                            {app.pricing_model === 'free' ? (
                                                'Free'
                                            ) : (
                                                <>
                                                    ${app.price}
                                                    {app.price_period && (
                                                        <span className="text-lg font-normal text-gray-600"> / {app.price_period}</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        {app.free_trial_days > 0 && (
                                            <p className="text-sm text-gray-600 mt-1">
                                                {app.free_trial_days}-day free trial available
                                            </p>
                                        )}
                                    </div>
                                    {isOwnApp ? (
                                        <div className="bg-gray-100 text-gray-600 px-8 py-3 rounded-lg flex items-center gap-2">
                                            <Shield className="w-5 h-5" />
                                            This is your app
                                        </div>
                                    ) : app.status === 0 ? (
                                        <div className="bg-red-100 text-red-800 px-8 py-3 rounded-lg flex items-center gap-2 font-semibold">
                                            <XCircle className="w-5 h-5" />
                                            App Deactivated
                                        </div>
                                    ) : (
                                        <button
                                            onClick={handlePurchase}
                                            className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                                        >
                                            <CreditCard className="w-5 h-5" />
                                            {app.pricing_model === 'free' ? 'Get License' : 'Purchase License'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Important Notice - Updated */}
                            <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-8">
                                <h4 className="font-semibold text-green-800 mb-2">✓ Secure Checkout</h4>
                                <ul className="text-sm text-green-700 space-y-1">
                                    <li>• Secure payment processing via Stripe</li>
                                    <li>• Instant license delivery to your email</li>
                                    <li>• Support for Credit Cards, Apple Pay & Google Pay</li>
                                    <li>• 30-day money-back guarantee</li>
                                </ul>
                            </div>

                            {/* Screenshots Section */}
                            {app.screenshots && app.screenshots.length > 0 && (
                                <div className="mb-8">
                                    <h2 className="text-xl font-semibold mb-4">Screenshots</h2>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {app.screenshots.map((screenshot, index) => (
                                            <div
                                                key={index}
                                                className="relative aspect-video group cursor-pointer overflow-hidden rounded-lg"
                                                onClick={() => {
                                                    // Open in a modal or new tab
                                                    window.open(screenshot, '_blank');
                                                }}
                                            >
                                                <img
                                                    src={screenshot}
                                                    alt={`${app.name} screenshot ${index + 1}`}
                                                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                                    loading="lazy"
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                                                    <div className="bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <ExternalLink className="w-5 h-5 text-gray-700" />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* App Details */}
                            <div className="space-y-6">
                                <section>
                                    <h2 className="text-xl font-semibold mb-3">About this app</h2>
                                    <p className="text-gray-600 leading-relaxed">
                                        {app.long_description || app.description}
                                    </p>
                                </section>

                                {app.screenshots && app.screenshots.length > 0 && (
                                    <section>
                                        <h2 className="text-xl font-semibold mb-3">Screenshots</h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {app.screenshots.map((screenshot, index) => (
                                                <div key={index} className="relative group cursor-pointer">
                                                    <img
                                                        src={screenshot}
                                                        alt={`${app.name} screenshot ${index + 1}`}
                                                        className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
                                                        onClick={() => window.open(screenshot, '_blank')}
                                                    />
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
                                                        <ExternalLink className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {app.key_features && app.key_features.length > 0 && (
                                    <section>
                                        <h2 className="text-xl font-semibold mb-3">Key Features</h2>
                                        <ul className="space-y-2">
                                            {app.key_features.map((feature, index) => (
                                                <li key={index} className="flex items-start gap-3">
                                                    <Check className="w-5 h-5 text-green-500 mt-0.5" />
                                                    <span className="text-gray-600">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </section>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar - unchanged */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow-lg p-6 sticky top-6">
                            <h3 className="text-lg font-semibold mb-4">App Information</h3>

                            <div className="space-y-4">
                                <div>
                                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                                        <User className="w-4 h-4" />
                                        <span className="text-sm">Developer</span>
                                    </div>
                                    <p className="font-medium">{app.company_name || 'Independent Developer'}</p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                                        <Calendar className="w-4 h-4" />
                                        <span className="text-sm">Listed On</span>
                                    </div>
                                    <p className="font-medium">{new Date(app.created_at).toLocaleDateString()}</p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                                        <Shield className="w-4 h-4" />
                                        <span className="text-sm">License Type</span>
                                    </div>
                                    <p className="font-medium capitalize">{app.pricing_model}</p>
                                </div>

                                {app.refund_policy && (
                                    <div>
                                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                                            <CreditCard className="w-4 h-4" />
                                            <span className="text-sm">Refund Policy</span>
                                        </div>
                                        <p className="font-medium">{app.refund_policy}</p>
                                    </div>
                                )}

                                <div className="pt-4 border-t space-y-2">
                                    {app.website_url && (
                                        <a
                                            href={app.website_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-blue-600 hover:underline"
                                        >
                                            <Globe className="w-4 h-4" />
                                            Visit Website
                                        </a>
                                    )}
                                    {app.documentation_url && (
                                        <a
                                            href={app.documentation_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-blue-600 hover:underline"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            Documentation
                                        </a>
                                    )}
                                    {app.support_url && (
                                        <a
                                            href={app.support_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-blue-600 hover:underline"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            Get Support
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Checkout Modal */}
            <CheckoutModal
                app={app}
                isOpen={showCheckout}
                onClose={() => setShowCheckout(false)}
            />
        </MarketplaceLayout>
    );
}